import { Box, Column, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { LineClamp } from '~/commonComponents';
import { IColumnDef } from '~/commonComponents/DataGrid';
import { InviteProfessional } from '~/commonComponents/InviteProfessional';
import { IDashboardJob } from '~/domain/DashboardJobs';
import { UserSubProfession } from '~/enums/UserProfession';
import { OutlineChevronRightIcon } from '@tempmee/react-icons';
import { Colors } from '~/themes/colors';

type TDataGridCommonColumn = {
  Action: IColumnDef<IDashboardJob>;
  Details: IColumnDef<IDashboardJob>;
  Invite: IColumnDef<IDashboardJob>;
  Office: IColumnDef<IDashboardJob>;
  Profession: IColumnDef<IDashboardJob>;
  Rate: IColumnDef<IDashboardJob>;
  Type: IColumnDef<IDashboardJob>;
};

export const DataGridCommonColumn: TDataGridCommonColumn = {
  Action: {
    key: 'action',
    header: '',
    width: '50px',
    align: 'center',
    render: () => <OutlineChevronRightIcon color={colors.secondary[500]} />,
  },
  Details: {
    key: 'details',
    header: 'Details',
    width: '192px',
    render: (item) => (
      <Column gap={4}>
        <Typography as="span" bold>
          {item.date}
        </Typography>
        <Typography as="span" color={colors.neutral[500]}>
          {item.startTime} - {item.endTime}
        </Typography>
      </Column>
    ),
  },
  Invite: {
    key: 'invite',
    header: 'Invite',
    width: '80px',
    align: 'center',
    render: (item) => {
      if (item.dashboardJobApi.isOnsiteInterview) {
        return null;
      }

      return <InviteProfessional shift={item.dashboardJobApi} size="icon" />;
    },
  },
  Office: {
    key: 'office',
    header: 'Office',
    width: '1fr',
    render: (item) => (
      <LineClamp lineClamp={2} text={item.office.name} style={{ color: colors.neutral[600] }} />
    ),
  },
  Profession: {
    key: 'profession',
    header: 'Profession',
    width: '192px',
    render: (item) => (
      <Column gap={4}>
        <Typography as="span" bold>
          {UserSubProfession.getName(item.subProfession ?? '') ?? item.professionName}
        </Typography>
        <Typography as="span" color={colors.neutral[500]}>
          {item.specialty?.split('_')?.[1] ?? 'General'}
        </Typography>
      </Column>
    ),
  },
  Rate: {
    key: 'rate',
    header: (
      <Typography
        as="span"
        bold
        color={colors.primary[500]}
        size="body_large"
        style={{ paddingLeft: 24 }}
      >
        Rate
      </Typography>
    ),
    width: '100px',
    render: (item) => (
      <Typography as="span" bold style={{ paddingLeft: 24 }}>
        ${item.rate.toFixed(2)}
      </Typography>
    ),
  },
  Type: {
    key: 'type',
    header: (
      <Typography
        as="span"
        bold
        color={colors.primary[500]}
        size="body_large"
        style={{ paddingLeft: 16 }}
      >
        Type
      </Typography>
    ),
    width: '120px',
    render: (item) => (
      <Box
        // Intrinsic pill styling — tight 2/10px padding + radius/colour are below the DS
        // spacing scale, so kept inline rather than snapped to a coarser token.
        style={{
          alignSelf: 'flex-start',
          padding: '2px 10px',
          borderRadius: 8,
          backgroundColor: item.dashboardJobApi.isOnsiteInterview
            ? Colors.jobs_interview_background
            : Colors.jobs_shift_background,
        }}
      >
        <Typography as="span" color={colors.neutral[500]}>
          {item.dashboardJobApi.isOnsiteInterview ? 'INTERVIEW' : 'SHIFT'}
        </Typography>
      </Box>
    ),
  },
};
